import { Helmet } from "react-helmet-async";
import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import Iconify from "../components/iconify";
import { post } from "../utils/networkUtils";

export default function NotificationsPage() {
  const [loading, setLoading] = useState({
    remind: false,
    weekly: false,
  });
  const [results, setResults] = useState({
    remind: null,
    weekly: null,
  });
  const [errors, setErrors] = useState({
    remind: null,
    weekly: null,
  });

  const handleSendRemindNotification = async () => {
    setLoading((prev) => ({ ...prev, remind: true }));
    setErrors((prev) => ({ ...prev, remind: null }));
    setResults((prev) => ({ ...prev, remind: null }));

    try {
      const response = await post("/admin/notifications/remind");
      setResults((prev) => ({
        ...prev,
        remind: response,
      }));
    } catch (error) {
      console.error("Error sending remind notification:", error);
      setErrors((prev) => ({
        ...prev,
        remind: error.response?.data?.error || error.message || "Failed to send notifications",
      }));
    } finally {
      setLoading((prev) => ({ ...prev, remind: false }));
    }
  };

  const handleSendWeeklyRemindNotification = async () => {
    setLoading((prev) => ({ ...prev, weekly: true }));
    setErrors((prev) => ({ ...prev, weekly: null }));
    setResults((prev) => ({ ...prev, weekly: null }));

    try {
      const response = await post("/admin/notifications/remind/weekly");
      setResults((prev) => ({
        ...prev,
        weekly: response,
      }));
    } catch (error) {
      console.error("Error sending weekly remind notification:", error);
      setErrors((prev) => ({
        ...prev,
        weekly: error.response?.data?.error || error.message || "Failed to send notifications",
      }));
    } finally {
      setLoading((prev) => ({ ...prev, weekly: false }));
    }
  };

  return (
    <>
      <Helmet>
        <title> Notifications Management </title>
      </Helmet>

      <Container maxWidth="lg">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 5 }}
        >
          <Box>
            <Typography variant="h4" gutterBottom>
              Notifications Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manually trigger notification campaigns
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={3}>
          {/* Remind Notification Card */}
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Iconify icon="eva:bell-outline" width={24} />
                  <Typography variant="h6">
                    Inactive Users Reminder
                  </Typography>
                </Stack>
                
                <Typography variant="body2" color="text.secondary">
                  Send reminder notifications to users who have been inactive for 3, 7, and 10 days.
                  This will send targeted notifications to re-engage inactive users.
                </Typography>

                {results.remind && (
                  <Alert severity="success">
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      {results.remind.message}
                    </Typography>
                    {results.remind.results && (
                      <Stack spacing={0.5}>
                        {results.remind.results.map((result, index) => (
                          <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip 
                              label={`${result.days} days`} 
                              size="small" 
                              color={result.success ? "success" : "error"}
                            />
                            <Typography variant="caption">
                              {result.userCount} users
                              {result.error && ` - ${result.error}`}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Alert>
                )}

                {errors.remind && (
                  <Alert severity="error">
                    {errors.remind}
                  </Alert>
                )}

                <Button
                  variant="contained"
                  startIcon={
                    loading.remind ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Iconify icon="eva:paper-plane-outline" />
                    )
                  }
                  onClick={handleSendRemindNotification}
                  disabled={loading.remind}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {loading.remind ? "Sending..." : "Send Inactive Users Reminder"}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Weekly Remind Notification Card */}
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Iconify icon="eva:calendar-outline" width={24} />
                  <Typography variant="h6">
                    Weekly Reminder
                  </Typography>
                </Stack>
                
                <Typography variant="body2" color="text.secondary">
                  Send weekend reminder notifications to users who haven't made picks today.
                  Message: "🔥 It's almost game time - secure your picks for the weekend!"
                </Typography>

                {results.weekly && (
                  <Alert severity="success">
                    <Typography variant="body2">
                      {results.weekly.message}
                    </Typography>
                    {results.weekly.userCount !== undefined && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        Sent to {results.weekly.userCount} users
                      </Typography>
                    )}
                  </Alert>
                )}

                {errors.weekly && (
                  <Alert severity="error">
                    {errors.weekly}
                  </Alert>
                )}

                <Button
                  variant="contained"
                  startIcon={
                    loading.weekly ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Iconify icon="eva:paper-plane-outline" />
                    )
                  }
                  onClick={handleSendWeeklyRemindNotification}
                  disabled={loading.weekly}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {loading.weekly ? "Sending..." : "Send Weekly Reminder"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </>
  );
}
